import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api } from "../lib/api";
import { Card, CardContent } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { CategoryBadge } from "../components/Badges";
import { relTime } from "../lib/labels";
import { ConfirmDialog } from "../components/ConfirmDialog";
import { ArrowLeft, ArrowCounterClockwise, Trash, TrashSimple, Warning } from "@phosphor-icons/react";
import { toast } from "sonner";

export default function KBTrash() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [purge, setPurge] = useState(null); // {id, title}

  const load = () => api.get("/kb/trash").then((r) => { setItems(r.data); setLoading(false); });
  useEffect(() => { load(); }, []);

  const restore = async (a) => {
    try {
      await api.post(`/kb/articles/${a.id}/restore`);
      toast.success(`« ${a.title} » restauré`);
      load();
    } catch (e) { toast.error(e.response?.data?.detail || "Erreur"); }
  };

  const doPurge = async () => {
    if (!purge) return;
    try {
      await api.delete(`/kb/articles/${purge.id}/purge`);
      toast.success("Article supprimé définitivement");
      setPurge(null);
      load();
    } catch (e) { toast.error(e.response?.data?.detail || "Erreur"); }
  };

  const daysLeft = (deletedAt) => {
    try {
      const d = new Date(deletedAt);
      const remaining = 30 - Math.floor((Date.now() - d.getTime()) / (1000 * 60 * 60 * 24));
      return Math.max(0, remaining);
    } catch { return 30; }
  };

  return (
    <div className="max-w-4xl mx-auto space-y-6">
      <div>
        <Link to="/kb" className="text-sm text-muted-foreground flex items-center gap-1" data-testid="back-kb">
          <ArrowLeft size={16}/> Base de connaissances
        </Link>
        <div className="flex items-center gap-3 mt-3">
          <div className="w-11 h-11 rounded-lg bg-destructive/10 text-destructive flex items-center justify-center">
            <TrashSimple size={22} weight="duotone"/>
          </div>
          <div>
            <div className="label-caps">Corbeille</div>
            <h1 className="font-display text-3xl md:text-4xl font-bold">Articles supprimés</h1>
          </div>
        </div>
        <p className="text-sm text-muted-foreground mt-3">
          Les articles restent en corbeille <b>30 jours</b> avant suppression définitive automatique. Vous pouvez les restaurer ou les purger manuellement.
        </p>
      </div>

      {loading ? <div className="text-muted-foreground">Chargement…</div> : (
        items.length === 0 ? (
          <Card>
            <CardContent className="pt-10 pb-10 text-center">
              <TrashSimple size={40} weight="duotone" className="mx-auto text-muted-foreground opacity-40"/>
              <p className="text-muted-foreground mt-3">La corbeille est vide.</p>
            </CardContent>
          </Card>
        ) : (
          <div className="space-y-3" data-testid="kb-trash-list">
            {items.map((a) => {
              const dl = daysLeft(a.deleted_at);
              const critical = dl <= 3;
              return (
                <Card key={a.id} data-testid={`trash-item-${a.id}`}>
                  <CardContent className="pt-5 pb-5 flex items-center justify-between gap-4 flex-wrap">
                    <div className="flex-1 min-w-0">
                      <CategoryBadge value={a.category}/>
                      <h3 className="font-display text-lg font-semibold mt-2 truncate">{a.title}</h3>
                      <div className="text-xs text-muted-foreground mt-1 flex items-center gap-2 flex-wrap">
                        <span>Supprimé {relTime(a.deleted_at)}</span>
                        <span>·</span>
                        <span className={critical ? "text-destructive font-semibold inline-flex items-center gap-1" : ""}>
                          {critical && <Warning size={12}/>}
                          Purge automatique dans {dl} jour{dl > 1 ? "s" : ""}
                        </span>
                        {a.author_name && <><span>·</span><span>{a.author_name}</span></>}
                      </div>
                    </div>
                    <div className="flex items-center gap-2">
                      <Button size="sm" variant="secondary" onClick={() => restore(a)} data-testid={`restore-${a.id}`}>
                        <ArrowCounterClockwise size={14} className="mr-1.5"/> Restaurer
                      </Button>
                      <Button size="sm" variant="destructive"
                        onClick={() => setPurge({ id: a.id, title: a.title })}
                        data-testid={`purge-${a.id}`}>
                        <Trash size={14} className="mr-1.5"/> Purger
                      </Button>
                    </div>
                  </CardContent>
                </Card>
              );
            })}
          </div>
        )
      )}

      <ConfirmDialog
        open={!!purge}
        onOpenChange={(v) => !v && setPurge(null)}
        title="Suppression définitive"
        description={purge && `« ${purge.title} » sera supprimé de manière irréversible. Cette action ne peut pas être annulée.`}
        confirmLabel="Supprimer définitivement"
        danger
        onConfirm={doPurge}
        testIdPrefix="purge-confirm"
      />
    </div>
  );
}
